import { idb } from "../db/index.ts";
import { g, helpers } from "../util/index.ts";
import type { PlayoffSeries, UpdateEvents, ViewInput } from "../../common/types.ts";
import {
	getPlayoffRoundStats,
	ROUND_STAT_KEYS,
	type RoundStatsRow,
} from "../core/season/playoffRoundStats.ts";
import { PlayersCache } from "../db/PlayersCache.ts";

const abbrevFor = (team: { tid: number; abbrev?: string }) =>
	team.abbrev ?? g.get("teamInfoCache")[team.tid]?.abbrev ?? "?";
const nameFor = (team: { tid: number; region?: string; name?: string }) =>
	team.region
		? `${team.region} ${(team as any).name ?? ""}`.trim()
		: `${g.get("teamInfoCache")[team.tid]?.region ?? ""} ${g.get("teamInfoCache")[team.tid]?.name ?? ""}`.trim();

const roundLabel = (round: number, numRounds: number, season: number) =>
	helpers.upperCaseFirstLetter(
		helpers.playoffRoundName(
			round,
			numRounds,
			// Two conferences meet in the final
			g.get("playoffsByConf", season) ? 2 : false,
		),
	);

// Best batter and best pitcher of a set of rows, to headline a series
const highlights = (rows: RoundStatsRow[]) => {
	const batter = [...rows]
		.filter((r) => r.pa > 0)
		.sort((a, b) => b.hr * 4 + b.h + b.rbi - (a.hr * 4 + a.h + a.rbi))[0];
	const pitcher = [...rows]
		.filter((r) => r.outs > 0)
		.sort((a, b) => b.soPit - b.er * 2 - (a.soPit - a.er * 2) || b.outs - a.outs)[0];
	return { batter, pitcher };
};

const updateFinals = async (
	inputs: ViewInput<"finals">,
	updateEvents: UpdateEvents,
	state: any,
) => {
	if (
		!updateEvents.includes("firstRun") &&
		!updateEvents.includes("gameSim") &&
		!updateEvents.includes("newPhase") &&
		inputs.season === state.season
	) {
		return;
	}

	const allSeries = (await idb.getCopies.playoffSeries({})) as (PlayoffSeries & {
		roundStats?: any;
	})[];
	allSeries.sort((a, b) => b.season - a.season);

	// Cache only within this request. The selected season/ALL and the records
	// table reuse the same data without keeping stale results after new games.
	const roundCache = new Map<string, ReturnType<typeof getPlayoffRoundStats>>();
	const getRoundStats = (series: PlayoffSeries, round: number) => {
		const key = `${series.season}:${round}`;
		if (!roundCache.has(key)) roundCache.set(key, getPlayoffRoundStats(series, round));
		return roundCache.get(key)!;
	};

	// History of every final
	const history = [];
	for (const ps of allSeries) {
		const numRounds = ps.series.length;
		const final = ps.series[numRounds - 1]?.[0];
		if (!final?.away) {
			continue;
		}
		const numGames = g.get("numGamesPlayoffSeries", ps.season)[numRounds - 1] ?? 7;
		const toWin = Math.ceil(numGames / 2);
		const done = final.home.won >= toWin || final.away.won >= toWin;
		const homeWon = final.home.won > final.away.won;
		const champion = homeWon ? final.home : final.away;
		const runnerUp = homeWon ? final.away : final.home;
		history.push({
			season: ps.season,
			done,
			champion: {
				tid: champion.tid,
				abbrev: abbrevFor(champion),
				name: nameFor(champion),
				seed: champion.seed,
				record: champion.regularSeason,
			},
			runnerUp: {
				tid: runnerUp.tid,
				abbrev: abbrevFor(runnerUp),
				name: nameFor(runnerUp),
				seed: runnerUp.seed,
				record: runnerUp.regularSeason,
			},
			result: `${Math.max(final.home.won, final.away.won)}-${Math.min(final.home.won, final.away.won)}`,
		});
	}

	// The chosen season in detail
	const season =
		inputs.season ?? allSeries.find((ps) => ps.series.at(-1)?.[0]?.away)?.season;
	const ps = allSeries.find((row) => row.season === season);
	let detail;
	if (ps) {
		const numRounds = ps.series.length;
		const rounds = [];
		for (let round = 0; round < numRounds; round++) {
			const { rows, source } = await getRoundStats(ps, round);
			rounds.push({
				round,
				label: roundLabel(round, numRounds, ps.season),
				series: ps.series[round]!.filter((s) => s.away).map((s) => ({
					home: { abbrev: abbrevFor(s.home), seed: s.home.seed, won: s.home.won, tid: s.home.tid },
					away: { abbrev: abbrevFor(s.away!), seed: s.away!.seed, won: s.away!.won, tid: s.away!.tid },
				})),
				stats: rows.map((row) => ({
					...row,
					season: ps.season,
					abbrev: g.get("teamInfoCache")[row.tid]?.abbrev ?? "?",
				})),
				source,
				coveredSeasons: source === "missing" ? [] : [ps.season],
				missingSeasons: source === "missing" ? [ps.season] : [],
				highlights: highlights(rows),
			});
		}

		detail = {
			season: ps.season as number | "all",
			rounds,
		};
	}

	if (season === "all" && allSeries.length > 0) {
		const maxRounds = Math.max(...allSeries.map((series) => series.series.length));
		const groups = new Map<number, {
			stats: (RoundStatsRow & { season: number; abbrev: string })[];
			coveredSeasons: number[];
			missingSeasons: number[];
		}>();
		for (const series of allSeries) {
			for (let round = 0; round < series.series.length; round++) {
				// Align historical formats from the final backwards: an old
				// three-round final is never mixed with a newer conference final.
				const stage = series.series.length - 1 - round;
				let group = groups.get(stage);
				if (!group) {
					group = { stats: [], coveredSeasons: [], missingSeasons: [] };
					groups.set(stage, group);
				}
				const { rows, source } = await getRoundStats(series, round);
				if (source === "missing") group.missingSeasons.push(series.season);
				else group.coveredSeasons.push(series.season);
				for (const row of rows) {
					const team = series.series[round]!.flatMap((s) => [s.home, s.away]).find((t) => t?.tid === row.tid);
					const abbrev = abbrevFor(team ?? { tid: row.tid });
					// One row per player AND season: the same player in two finals is two rows, each with its year
					const existing = group.stats.find((p) => p.pid === row.pid && p.season === series.season);
					if (!existing) {
						group.stats.push({ ...row, season: series.season, abbrev });
					} else {
						for (const key of ROUND_STAT_KEYS) {
							existing[key] += row[key];
						}
						existing.abKnown = existing.abKnown !== false && row.abKnown !== false;
						existing.tid = row.tid;
						existing.abbrev = abbrev;
					}
				}
			}
		}
		const rounds = [...groups.entries()].sort(([a], [b]) => b - a).map(([stage, group], index) => ({
			round: index,
			label: roundLabel(maxRounds - 1 - stage, maxRounds, allSeries[0]!.season),
			series: [],
			stats: group.stats,
			source: (group.stats.length > 0 ? "recorded" : "missing") as "recorded" | "missing",
			coveredSeasons: group.coveredSeasons,
			missingSeasons: group.missingSeasons,
			highlights: highlights(group.stats),
		}));
		detail = { season: "all", rounds };
	}

	// Franchise records over the whole history of finals
	const franchises = new Map<
		number,
		{ tid: number; abbrev: string; name: string; titles: number; finals: number; runnerUp: number; sweeps: number; gamesWon: number; gamesLost: number; lastTitle?: number }
	>();
	const franchise = (tid: number) => {
		let row = franchises.get(tid);
		if (!row) {
			row = { tid, abbrev: abbrevFor({ tid }), name: nameFor({ tid }), titles: 0, finals: 0, runnerUp: 0, sweeps: 0, gamesWon: 0, gamesLost: 0 };
			franchises.set(tid, row);
		}
		return row;
	};
	let sweepsTotal = 0;
	let fullLength = 0;
	for (const ps2 of allSeries) {
		const numRounds = ps2.series.length;
		const final = ps2.series[numRounds - 1]?.[0];
		if (!final?.away) {
			continue;
		}
		const numGames = g.get("numGamesPlayoffSeries", ps2.season)[numRounds - 1] ?? 7;
		const toWin = Math.ceil(numGames / 2);
		if (final.home.won < toWin && final.away.won < toWin) {
			continue;
		}
		const [champ, runner] =
			final.home.won > final.away.won ? [final.home, final.away] : [final.away, final.home];
		const c = franchise(champ.tid);
		const r = franchise(runner.tid);
		c.titles += 1;
		c.finals += 1;
		c.lastTitle = Math.max(c.lastTitle ?? 0, ps2.season);
		r.finals += 1;
		r.runnerUp += 1;
		c.gamesWon += champ.won;
		c.gamesLost += runner.won;
		r.gamesWon += runner.won;
		r.gamesLost += champ.won;
		if (runner.won === 0) {
			c.sweeps += 1;
			sweepsTotal += 1;
		}
		if (champ.won + runner.won === numGames) {
			fullLength += 1;
		}
	}

	// Finals MVPs, from the saved awards (the finals series award)
	const playersCache = new PlayersCache();
	const mvps = new Map<number, { pid: number; name: string; count: number; seasons: number[] }>();
	for (const a of await idb.getCopies.awards(undefined, "noCopyCache")) {
		for (const award of a.awards) {
			if (award.numTeams !== undefined || award.statRange !== -1) {
				continue;
			}
			const pid = award.winner[0]?.pid;
			if (pid === undefined) {
				continue;
			}
			let row = mvps.get(pid);
			if (!row) {
				const p = await playersCache.get(pid);
				row = { pid, name: p ? `${p.firstName} ${p.lastName}` : `#${pid}`, count: 0, seasons: [] };
				mvps.set(pid, row);
			}
			row.count += 1;
			row.seasons.push(a.season);
		}
	}

	// Finals records over every season with player data for the final
	type Rec = { value: number; name: string; pid: number; season: number; abbrev: string };
	const seriesRecords: Record<string, Rec | undefined> = {};
	const career = new Map<number, { pid: number; name: string; seasons: number } & Record<string, number>>();
	const recordStats = ["h", "hr", "rbi", "r", "sb", "soPit", "w"] as const;
	const coveredSeasons: number[] = [];
	const gameRecords: Record<string, Rec | undefined> = {};
	for (const row of allSeries) {
		const numRounds = row.series.length;
		if (!row.series[numRounds - 1]?.[0]?.away) {
			continue;
		}
		const { rows, source } = await getRoundStats(row, numRounds - 1);
		if (source === "missing") {
			continue;
		}
		coveredSeasons.push(row.season);
		for (const r of rows) {
			const abbrev = g.get("teamInfoCache")[r.tid]?.abbrev ?? "?";
			for (const stat of recordStats) {
				if (r[stat] > (seriesRecords[stat]?.value ?? 0)) {
					seriesRecords[stat] = { value: r[stat], name: r.name, pid: r.pid, season: row.season, abbrev };
				}
			}
			const c = career.get(r.pid) ?? ({ pid: r.pid, name: r.name, seasons: 0 } as any);
			c.seasons += 1;
			for (const stat of recordStats) {
				c[stat] = (c[stat] ?? 0) + r[stat];
			}
			career.set(r.pid, c);
		}
		// Single game records need box scores
		const gids = row.series[numRounds - 1]![0]!.gids ?? [];
		if (gids.length > 0) {
			for (const game of await idb.getCopies.games({ gids }, "noCopyCache")) {
				for (const t of game.teams) {
					for (const p of t.players as any[]) {
						for (const stat of recordStats) {
							if ((p[stat] ?? 0) > (gameRecords[stat]?.value ?? 0)) {
								gameRecords[stat] = {
									value: p[stat],
									name: p.name,
									pid: p.pid,
									season: row.season,
									abbrev: g.get("teamInfoCache")[t.tid]?.abbrev ?? "?",
								};
							}
						}
					}
				}
			}
		}
	}
	const careerRecords: Record<string, Rec | undefined> = {};
	for (const c of career.values()) {
		for (const stat of recordStats) {
			if ((c[stat] ?? 0) > (careerRecords[stat]?.value ?? 0)) {
				careerRecords[stat] = { value: c[stat]!, name: c.name, pid: c.pid, season: c.seasons, abbrev: "" };
			}
		}
	}

	return {
		season,
		history,
		detail,
		franchiseRecords: [...franchises.values()],
		finalsMvps: [...mvps.values()],
		finalsSummary: { finals: history.filter((h) => h.done).length, sweeps: sweepsTotal, fullLength },
		records: {
			stats: recordStats as unknown as string[],
			game: gameRecords,
			series: seriesRecords,
			career: careerRecords,
			coveredSeasons: coveredSeasons.sort((a, b) => a - b),
		},
	};
};

export default updateFinals;
